import { z } from 'zod';
import { ANIMATED_SHEET_KEYS, DOOR_SHEET_KEYS } from './animated-sheets.js';
import {
  ANIMATION_TYPE,
  OBJECT_TYPES,
  SPRITE_MAX_TILES,
  SPRITE_TYPE,
  type ObjectType,
} from './rules.js';
import { type TiledObject, type TiledProperty } from './types.js';

/**
 * Typed custom properties of map objects (spec §6.9.2 "properties valid").
 * One description per field drives both the validation (zod) and the editor's inspector form, so
 * the two can never disagree.
 */
export type PropertyField =
  | {
      readonly name: string;
      readonly kind: 'string';
      readonly max: number;
      /** Machine-written data the inspector does not show (e.g. a sprite's tile ids). */
      readonly hidden?: boolean;
    }
  | { readonly name: string; readonly kind: 'boolean'; readonly default?: boolean }
  | {
      readonly name: string;
      readonly kind: 'number';
      readonly min: number;
      readonly max: number;
      readonly integer?: boolean;
    }
  | { readonly name: string; readonly kind: 'enum'; readonly options: readonly string[] };

/**
 * Whether furniture of each type blocks movement when its `blocking` property is not set.
 * Solid, free-standing furniture blocks. Things you walk through or stand on (doors, teleporters,
 * dance floor, cat spot), sit on (chairs), or that hang on a wall or stand on a table do not.
 */
export const BLOCKING_DEFAULTS: Readonly<Record<ObjectType, boolean>> = {
  desk: true,
  computer: true,
  coffee_machine: true,
  beer_tap: true,
  fridge: true,
  whiteboard: true,
  wheel: true,
  ping_pong_table: true,
  chair: false,
  door: false,
  window: false,
  candy_bowl: false,
  wall_calendar: false,
  bulletin_board: false,
  dance_floor: false,
  cat_spot: false,
  cake_spot: false,
  teleporter: false,
  coop_door: true,
  toggle_door: true,
  pin_door: true,
  lamp: false,
};

/** The `blocking` default of an object type (sprites block; unknown types do not). */
export function blocksByDefault(type: string): boolean {
  if (type === SPRITE_TYPE) return true;
  return (BLOCKING_DEFAULTS as Readonly<Record<string, boolean>>)[type] ?? false;
}

/** Whether an object blocks movement: its `blocking` property, else its type's default. */
export function isBlockingObject(object: TiledObject, type: string): boolean {
  const value = object.properties?.find((p) => p.name === 'blocking')?.value;
  return typeof value === 'boolean' ? value : blocksByDefault(type);
}

/** Which animated sheet draws the door; empty means the default door (ADR-0043). */
const DOOR_SHEET_FIELD: PropertyField = {
  name: 'door_sheet',
  kind: 'enum',
  options: ['', ...DOOR_SHEET_KEYS],
};

const TYPE_FIELDS: Partial<Record<ObjectType, readonly PropertyField[]>> = {
  desk: [{ name: 'owner', kind: 'string', max: 80 }],
  // Moves the player to the spawn point named `target_spawn` on the same map (ADR-0037).
  // `target_map` is reserved for teleporting between maps, which is not supported.
  teleporter: [
    { name: 'target_map', kind: 'string', max: 80 },
    { name: 'target_spawn', kind: 'string', max: 80 },
  ],
  // Players that must hold the door together, and how long it stays open (ADR-0038).
  coop_door: [
    { name: 'required', kind: 'number', min: 2, max: 8, integer: true },
    { name: 'open_seconds', kind: 'number', min: 1, max: 60, integer: true },
    DOOR_SHEET_FIELD,
  ],
  // Anyone presses E to open or close it (ADR-0041).
  toggle_door: [DOOR_SHEET_FIELD],
  // A door locked behind a 4 digit PIN, then opened and closed like a plain door. The PIN itself
  // is server configuration (`DOOR_PINS`, keyed by the object's name) so it never ships to clients
  // inside the map (ADR-0039).
  pin_door: [DOOR_SHEET_FIELD],
  // Glow radius in tiles; lamps light up after sunset (spec §6.10).
  lamp: [{ name: 'radius', kind: 'number', min: 1, max: 10, integer: true }],
  // Anyone may clear the board, not only admins (spec §6.15).
  whiteboard: [{ name: 'public_clear', kind: 'boolean', default: false }],
};

/** Fields by object type. Types without an entry have no properties. */
export const PROPERTY_FIELDS: Readonly<Record<string, readonly PropertyField[]>> = {
  // Every furniture type can override whether it blocks movement.
  ...Object.fromEntries(
    OBJECT_TYPES.map((type): [string, readonly PropertyField[]] => [
      type,
      [
        ...(TYPE_FIELDS[type] ?? []),
        { name: 'blocking', kind: 'boolean', default: BLOCKING_DEFAULTS[type] },
      ],
    ]),
  ),
  // Sprites (layer `sprites`, ADR-0022): the bottom `base_rows` rows are the footprint.
  [SPRITE_TYPE]: [
    // Rows of tile ids, "1,2;3,4" (rows split by `;`); 16x16 ids of up to 10 digits fit.
    { name: 'tiles', kind: 'string', max: 3000, hidden: true },
    { name: 'base_rows', kind: 'number', min: 1, max: SPRITE_MAX_TILES, integer: true },
    { name: 'blocking', kind: 'boolean', default: true },
  ],
  // Animated objects (layer `animations`, ADR-0040): which catalog picture loops here.
  [ANIMATION_TYPE]: [{ name: 'sheet', kind: 'enum', options: ANIMATED_SHEET_KEYS }],
  // Regions (layer `zones`, type `region`): where the area is, for its own weather (ADR-0037).
  region: [
    { name: 'latitude', kind: 'number', min: -90, max: 90 },
    { name: 'longitude', kind: 'number', min: -180, max: 180 },
  ],
  // Zones (layer `zones`, type `zone`).
  zone: [
    { name: 'chat_zone', kind: 'boolean' },
    { name: 'ambience', kind: 'enum', options: ['', 'indoor', 'kitchen', 'lounge', 'outdoor'] },
  ],
};

export function fieldsFor(type: string): readonly PropertyField[] {
  return PROPERTY_FIELDS[type] ?? [];
}

const fieldSchema = (field: PropertyField): z.ZodTypeAny => {
  switch (field.kind) {
    case 'string':
      return z.string().max(field.max);
    case 'boolean':
      return z.boolean();
    case 'number':
      return (field.integer ? z.number().int() : z.number()).min(field.min).max(field.max);
    case 'enum':
      return z.enum(field.options as [string, ...string[]]);
  }
};

/** zod schema of an object type's properties (unknown extra properties are allowed). */
export function propertySchema(type: string): z.ZodTypeAny {
  const shape: Record<string, z.ZodTypeAny> = {};
  for (const field of fieldsFor(type)) shape[field.name] = fieldSchema(field).optional();
  return z.object(shape).passthrough();
}

/** Tiled's property array as a plain record. */
export function readProperties(object: {
  properties?: TiledProperty[];
}): Record<string, string | number | boolean> {
  return Object.fromEntries((object.properties ?? []).map((p) => [p.name, p.value]));
}

/** Problems with an object's properties, as readable messages (empty when valid). */
export function checkProperties(type: string, object: TiledObject): string[] {
  const result = propertySchema(type).safeParse(readProperties(object));
  if (result.success) return [];
  return result.error.issues.map((issue) => `${issue.path.join('.')}: ${issue.message}`);
}

const TILED_TYPE: Record<PropertyField['kind'], string> = {
  string: 'string',
  enum: 'string',
  boolean: 'bool',
  number: 'float',
};

/** Returns the properties with `name` set (or removed when `value` is `undefined`). */
export function withProperty(
  properties: readonly TiledProperty[] | undefined,
  field: PropertyField,
  value: string | number | boolean | undefined,
): TiledProperty[] {
  const others = (properties ?? []).filter((p) => p.name !== field.name);
  return value === undefined
    ? others
    : [
        ...others,
        {
          name: field.name,
          type: field.kind === 'number' && field.integer ? 'int' : TILED_TYPE[field.kind],
          value,
        },
      ];
}
